import React from 'react';
import '../../css/Prices.css';
import TituloPagina from '../../js/Components/PageTitle';

type PriceItem = {
  key: string;
  category: string;
  name: string;
  price?: number;
  min: number;
  max: number;
  average: number;
  branches: number;
  branchNames?: string;
  hasBranchVariation?: boolean;
  note?: string;
};

type PriceResponse = {
  ok: boolean;
  disclaimer?: string;
  items?: PriceItem[];
};

const fallbackItems: PriceItem[] = [
  { key: 'limpieza_dental', category: 'Diagnóstico y prevención', name: 'Limpieza dental / profilaxis', min: 0, max: 0, average: 0, branches: 0, branchNames: '' },
  { key: 'consulta_valoracion', category: 'Diagnóstico y prevención', name: 'Consulta de valoración', min: 0, max: 0, average: 0, branches: 0, branchNames: '' },
  { key: 'resina_simple', category: 'Operatoria dental', name: 'Resina simple', min: 0, max: 0, average: 0, branches: 0, branchNames: '' },
  { key: 'endodoncia_molar', category: 'Endodoncia', name: 'Endodoncia molar', min: 0, max: 0, average: 0, branches: 0, branchNames: '' },
  { key: 'brackets_metalicos', category: 'Ortodoncia', name: 'Brackets metálicos', min: 0, max: 0, average: 0, branches: 0, branchNames: '' },
  { key: 'puente_fijo', category: 'Prótesis y rehabilitación', name: 'Puente fijo', min: 0, max: 0, average: 0, branches: 0, branchNames: '' },
];

const formatMoney = (value: number): string =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(value);

const renderPrice = (item: PriceItem): string => {
  const publicPrice = item.price || item.min || item.average || 0;
  if (!publicPrice) return 'Por valorar';
  return `Desde ${formatMoney(publicPrice)}`;
};

const Prices: React.FC = () => {
  const [items, setItems] = React.useState<PriceItem[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [query, setQuery] = React.useState<string>('');
  const [category, setCategory] = React.useState<string>('Todos');
  const [hasLiveData, setHasLiveData] = React.useState<boolean>(true);

  React.useEffect(() => {
    let mounted = true;
    fetch('/admin/api/precios_public.php')
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data: PriceResponse) => {
        if (!mounted) return;
        const liveItems = Array.isArray(data.items) ? data.items : [];
        setItems(liveItems.length > 0 ? liveItems : fallbackItems);
        setHasLiveData(liveItems.length > 0);
      })
      .catch(() => {
        if (!mounted) return;
        setItems(fallbackItems);
        setHasLiveData(false);
      })
      .finally(() => mounted && setIsLoading(false));
    return () => { mounted = false; };
  }, []);

  const categories = React.useMemo(() => ['Todos', ...Array.from(new Set(items.map((item) => item.category)))], [items]);
  const filteredItems = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return items.filter((item) => {
      const matchesCategory = category === 'Todos' || item.category === category;
      const matchesQuery = normalizedQuery === '' || `${item.name} ${item.category}`.toLowerCase().includes(normalizedQuery);
      return matchesCategory && matchesQuery;
    });
  }, [items, query, category]);

  return (
    <div>
      <TituloPagina title="Precios estimados | DentalPuente" pageSlug="prices" />
      <main className="prices-page content-wrap">
        <section className="prices-hero">
          <div>
            <span className="dp-eyebrow">Tarifario estimado</span>
            <h1>Precios dentales de referencia</h1>
            <p>Consulta precios aproximados antes de tu valoración. Mostramos un estimado simple desde el precio activo más accesible entre sucursales.</p>
          </div>
          <aside className="prices-warning-card" aria-label="Aviso importante de precios">
            <strong>Importante</strong>
            <span>Los precios pueden variar dependiendo la sucursal, el trabajo realizado, materiales, complejidad y valoración de la odontóloga.</span>
          </aside>
        </section>

        <section className="prices-filter-panel" aria-label="Filtros de precios">
          <div className="prices-search-box">
            <label htmlFor="priceSearch">Buscar tratamiento</label>
            <input id="priceSearch" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Ej. limpieza, brackets, endodoncia..." />
          </div>
          <div className="prices-category-box">
            <label htmlFor="priceCategory">Categoría</label>
            <select id="priceCategory" value={category} onChange={(event) => setCategory(event.target.value)}>
              {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
        </section>

        {!hasLiveData && !isLoading && (
          <div className="prices-empty-notice">
            <strong>Tarifario en preparación.</strong> Estos tratamientos son ejemplos visibles mientras el equipo carga precios por sucursal en administración.
          </div>
        )}

        <section className="prices-grid" aria-live="polite">
          {isLoading ? Array.from({ length: 6 }).map((_, index) => <article className="price-card price-card-loading" key={index} />) : filteredItems.map((item) => (
            <article className="price-card" key={item.key}>
              <div className="price-card-top">
                <span>{item.category}</span>
                <strong>{renderPrice(item)}</strong>
              </div>
              <h2>{item.name}</h2>
              <p>{item.branches > 0 ? item.hasBranchVariation ? 'Puede variar dependiendo la sucursal.' : 'Precio estimado sujeto a valoración clínica.' : 'Precio final bajo valoración clínica.'}</p>
              <small>{item.note || 'Disponible bajo valoración'}</small>
            </article>
          ))}
        </section>

        {!isLoading && filteredItems.length === 0 && (
          <section className="prices-no-results">
            <h2>No encontré tratamientos con esos filtros</h2>
            <p>Prueba con otra categoría o una búsqueda más general.</p>
          </section>
        )}

        <section className="prices-cta">
          <div>
            <span className="dp-eyebrow">Valoración profesional</span>
            <h2>Agenda tu cita para recibir un presupuesto real</h2>
            <p>El precio publicado es una referencia inicial; la odontóloga confirma el costo real después de revisar tu caso.</p>
          </div>
          <a className="dp-btn dp-btn-primary" href="/Contact">Agendar cita</a>
        </section>
      </main>
    </div>
  );
};

export default Prices;
